// Code generated by apic; DO NOT EDIT.
import type { RestEndpointMeta } from "../types";
import type { SampleContext } from "./shared";
import { expandPath, isUnsafe, resolveAuthMode, hasMTLSLeaf } from "./shared";

// ── Python-specific helper ────────────────────────────────────────────────────

/**
 * Produce a Python double-quoted string literal from a raw string value.
 * JSON.stringify escapes ", \, and control characters; Python and JSON share
 * the same basic escape sequences so the result is valid Python (F-4 appsec).
 */
export function pyStringLit(s: string): string {
  return JSON.stringify(s);
}

/**
 * pythonSample returns a Python requests snippet for the given endpoint.
 *
 * Auth injection follows the same rules as curlSample:
 *  - jwt / cookie → headers["Authorization"] = "Bearer <token>"
 *  - api_key      → headers["X-API-Key"] = "<api-key>"
 *  - mtls/cac/piv → comment only; requests(..., cert=(...))
 *  - public       → no auth header
 */
export function pythonSample(ep: RestEndpointMeta, ctx: SampleContext): string {
  const method = ep.method.toLowerCase();
  const expandedPath = expandPath(ep.path, ctx);
  const url = ctx.baseUrl.replace(/\/$/, "") + expandedPath;

  const lines: string[] = [];
  lines.push(`import requests`);
  lines.push(``);
  lines.push(`url = ${pyStringLit(url)}`);
  lines.push(`headers = {"Accept": "application/json"}`);

  const mode = resolveAuthMode(ep.auth);
  const composite = ep.auth.composite !== null;
  const mtlsLeaf = hasMTLSLeaf(ep.auth);
  const unsafe = isUnsafe(ep.method);

  if (mode === "jwt" || mode === "cookie") {
    const tok = ctx.token !== undefined && ctx.token !== "" ? ctx.token : "<token>";
    lines.push(`headers["Authorization"] = ${pyStringLit("Bearer " + tok)}`);
    if (ep.auth.csrf === "required" || ep.auth.csrf === "auto") {
      lines.push(`headers["X-CSRF-Token"] = "<csrf-token>"`);
    }
    if (composite && mtlsLeaf) {
      lines.push(`# also requires mTLS client certificate (pass cert=(...) to requests)`);
    }
  } else if (mode === "api_key") {
    const key = ctx.apiKey !== undefined && ctx.apiKey !== "" ? ctx.apiKey : "<api-key>";
    lines.push(`headers["X-API-Key"] = ${pyStringLit(key)}`);
    if (composite && mtlsLeaf) {
      lines.push(`# also requires mTLS client certificate (pass cert=(...) to requests)`);
    }
  } else if (mode === "mtls" || mode === "cac" || mode === "piv") {
    lines.push(`# requires a client certificate — cannot be expressed as a header`);
    lines.push(`# cert = ("/path/to/client.pem", "/path/to/client-key.pem")`);
  }

  lines.push(``);

  if (unsafe) {
    // Use json.loads() to deserialize the body so booleans/null render as valid
    // Python (True/False/None) rather than JSON true/false/null. pyStringLit
    // produces a valid Python double-quoted JSON string. (R2-3 fix.)
    const bodyJson = ctx.body !== undefined ? JSON.stringify(ctx.body) : "{}";
    const bodyLit = `json.loads(${pyStringLit(bodyJson)})`;
    lines.push(`import json`);
    lines.push(`headers["Content-Type"] = "application/json"`);
    lines.push(`body = ${bodyLit}`);
    if (mode === "mtls" || mode === "cac" || mode === "piv") {
      lines.push(`resp = requests.${method}(url, headers=headers, json=body, cert=cert)`);
    } else {
      lines.push(`resp = requests.${method}(url, headers=headers, json=body)`);
    }
  } else {
    if (mode === "mtls" || mode === "cac" || mode === "piv") {
      lines.push(`resp = requests.${method}(url, headers=headers, cert=cert)`);
    } else {
      lines.push(`resp = requests.${method}(url, headers=headers)`);
    }
  }

  lines.push(`resp.raise_for_status()`);
  lines.push(`print(resp.json())`);

  return lines.join("\n");
}
